import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { dequal } from "dequal";
import { DateTime } from "luxon";

export interface UserSelectionState {
  base: string;
  statTest: string;
  periodType: string;
  periodStartDate: string;
  periodEndDate: string;
  period: string;
  postPeriodType: string;
  postStartDate: string;
  postEndDate: string;
  mode:
    | "POINT_IN_TIME"
    | "TREND"
    | "PRE_POST"
    | "BUTTERFLY"
    | "PROFILE"
    | "PERCEPTUAL"
    | "DIFFERENTIATION";
  brands: (number | string)[]; // Multiple brands as array (string for brand sets)
  secondaryBrands: (number | string)[];
  markets: number[]; // Multiple markets as array
  segments: number[]; // Only plural key, no singular key
  filters: number[];
  groupedBy: string;
  questionOptions: Record<string, number[]>;
  visualizationType: string;
  ageRange: number[] | null;
  scoreType: string;
  profileMetric: number | null;
  profileGroup: number | null;
  profileResponse: number | null;
  trendsMetric: string | null;
  trendsResponse: string | null;
  viewId: string | null;
  hiddenSegments?: number[];
  hiddenBrands?: number[];
  screenType: string;
  hiddenMarkets?: number[];
  hiddenQuestions?: string[];
  module: string;
  reportId: string | null;
  /**
   * A fingerprint of the currently-selected brand set's member composition.
   *
   * Computed automatically by `useModeData` from the brand entity data in
   * `brandsSlice` — NOT written via `syncFromURL` or any explicit dispatch.
   *
   * When a brand set is edited (`updateBrand` dispatched), the entity's
   * `members` array changes.  `useModeData`'s selector re-computes this
   * fingerprint, `apiTriggeringSelection` in `useModeViewData` changes
   * (because `"brandSetVersion"` is in `API_TRIGGERING_KEYS`), and Effect 1
   * re-runs — calling the unified analytics API with the updated composition.
   *
   * This field is NOT serialised to the URL and is never written to Redux
   * directly; it exists only as a type anchor for the `API_TRIGGERING_KEYS`
   * set and the `useModeData` selector.
   */
  brandSetVersion?: string | number;
}

const initialState: UserSelectionState = {
  module: "visibility",
  reportId: null,
  base: "GENPOP",
  statTest: "POP",
  periodType: "L180D",
  screenType: "",
  mode: "POINT_IN_TIME",
  periodStartDate: DateTime.now().minus({ days: 180 }).toFormat("yyyy-MM-dd"),
  periodEndDate: DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd"),
  period: "MV_12",
  postPeriodType: "L180D",
  postStartDate: DateTime.now().minus({ days: 180 }).toFormat("yyyy-MM-dd"),
  postEndDate: DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd"),
  visualizationType: "",
  groupedBy: "RESPONSE",
  brands: [], // Multiple brands as array
  secondaryBrands: [],
  markets: [], // Multiple markets as array
  segments: [], // Only plural key, no singular key
  filters: [],
  questionOptions: {},
  ageRange: null,
  scoreType: "MEAN",
  profileGroup: null,
  profileMetric: null,
  profileResponse: null,
  trendsMetric: null,
  trendsResponse: null,
  viewId: null,
  hiddenSegments: [],
  hiddenBrands: [],
  hiddenMarkets: [],
};

interface UpdateFieldPayload {
  field: keyof UserSelectionState;
  value: string | string[];
}

export const userSelectionSlice = createSlice({
  name: "userSelection",
  initialState,
  reducers: {
    updateSelection: (
      state,
      action: PayloadAction<Partial<UserSelectionState>>
    ) => {
      // Update multiple fields at once
      return { ...state, ...action.payload };
    },
    updateField: (state, action: PayloadAction<UpdateFieldPayload>) => {
      // Update a single field
      const { field, value } = action.payload;
      (state as any)[field] = value;
    },
    resetSelection: () => {
      return initialState;
    },
    syncFromURL: (
      state: UserSelectionState,
      action: PayloadAction<Partial<UserSelectionState>>
    ) => {
      Object.entries(action.payload).forEach(([key, value]: [string, any]) => {
        //@ts-ignore
        if (!dequal(state[key], value)) {
          //@ts-ignore
          state[key] = value;
        }
      });
      return state;
    },
  },
});

export const { updateSelection, updateField, resetSelection, syncFromURL } =
  userSelectionSlice.actions;

export default userSelectionSlice.reducer;
